import {
  Content,
  DomainUser,
  GetLibrary,
  LocationACL,
  Profile,
  ResourceGrant,
  ResourceProfile,
} from '@raydiant/api-client-js';
import FolderIcon from './v2/assets/folder-icon.svg';
import FolderThumbnail from './v2/assets/folder-thumbnail.svg';
import UserThumbnail from './v2/assets/user-thumbnail.svg';
import LibraryIcon from './v2/assets/library-icon.svg';
import PlaylistThumbnail from './v2/assets/playlist-thumbnail.svg';
import PlaylistIcon from './v2/assets/playlist-icon.svg';
import { SortFolderOptions } from '../../utilities';
import _ from 'lodash';

export interface LibraryQueryParams {
  profile_id: string;
  folder_id: string;
  scope: string;
  search: string;
  sort_by: string;
  sort_direction: string;
  back_to: string;
  // Folder the user lands in via "See in Library"; the back-to-playlist
  // boundary (see LibraryPageV2). Empty string = library root.
  back_to_folder: string;
}

export type LayoutOptions = 'grid' | 'list';

// A "See in Library" back_to target must be an in-app path: history.push on a
// cross-origin URL throws a SecurityError, and scheme/protocol-relative URLs
// make no sense as a return target. Root-relative only (`/...`, not `//host`).
export const isSafeBackToPath = (path: string) =>
  path.startsWith('/') && !path.startsWith('//');

export const defaultLibraryPageQueryParams: LibraryQueryParams = {
  profile_id: '',
  folder_id: '',
  scope: 'owned',
  search: '',
  sort_by: 'name',
  sort_direction: 'asc',
  back_to: '',
  back_to_folder: '',
};

export const getQueryString = <T extends object>(queryObj: T): string => {
  return new URLSearchParams(
    Object.entries(queryObj as Record<string, unknown>)
      .filter(
        ([, value]) => value !== '' && value !== null && value !== undefined,
      )
      .map(([key, value]) => [key, String(value)]),
  ).toString();
};

export const DEFAULT_FOLDER_DATA = {
  applicationIconUrl: FolderIcon,
  applicationThumbnailUrl: FolderThumbnail,
  thumbnailUrl: FolderThumbnail,
  fileUploads: null,
};

export const DEFAULT_SHARED_LIBRARY_FOLDER_DATA = {
  applicationIconUrl: LibraryIcon,
  applicationThumbnailUrl: UserThumbnail,
  thumbnailUrl: UserThumbnail,
  fileUploads: null,
};

export const DEFAULT_PLAYLIST_DATA = {
  applicationIconUrl: PlaylistIcon,
  applicationThumbnailUrl: PlaylistThumbnail,
  thumbnailUrl: PlaylistThumbnail,
  fileUploads: null,
};

export const getPresentationData = (content: Content) => {
  switch (content.resourceType) {
    case 'presentation':
      return content.presentationData;
    case 'folder':
      return DEFAULT_FOLDER_DATA;
    case 'playlist':
      return DEFAULT_PLAYLIST_DATA;
    default:
      return content.presentationData;
  }
};

export const getLibraryName = (content: Content, profile: Profile) => {
  let libraryName = '';
  if (content.ownedBy.id === profile.id) {
    libraryName = 'My Library';
  } else if (content.acl.some((acl) => acl.grantProfile.id === profile.id)) {
    libraryName = 'Shared';
  } else {
    libraryName = content.ownedBy.name;
  }

  return libraryName;
};

export const sortContent = (
  data: Content[],
  sortOptions: SortFolderOptions,
) => {
  return [...data].sort((a, b) => {
    const valueA = a[sortOptions.property as keyof Content];
    const valueB = b[sortOptions.property as keyof Content];

    if (valueA === undefined || valueB === undefined) return 0;

    if (typeof valueA === 'string' && typeof valueB === 'string') {
      return sortOptions.direction === 'asc'
        ? valueA.localeCompare(valueB)
        : valueB.localeCompare(valueA);
    }

    if (typeof valueA === 'number' && typeof valueB === 'number') {
      return sortOptions.direction === 'asc'
        ? valueA - valueB
        : valueB - valueA;
    }

    return 0;
  });
};

export const mergeLibrary = (data: Content[]) => {
  const mergedMap = new Map<string, Content>();

  data.forEach((item) => {
    const existingItem = mergedMap.get(item.id);

    if (existingItem) {
      const mergedAclSet = new Map<string, LocationACL>();
      existingItem.acl?.forEach((acl) => mergedAclSet.set(acl.id, acl));
      item.acl?.forEach((acl) => mergedAclSet.set(acl.id, acl));
      existingItem.acl = Array.from(mergedAclSet.values());
    } else {
      mergedMap.set(item.id, { ...item, acl: item.acl || [] });
    }
  });

  let mergedArray = Array.from(mergedMap.values());

  return mergedArray;
};

export const getSortedLibraryData = (
  libraryData: GetLibrary | undefined,
  sortOptions: SortFolderOptions,
) => {
  if (!libraryData || libraryData?.data?.length === 0) return libraryData;

  const nextData = {
    ...libraryData,
    data: mergeLibrary(libraryData.data || []),
  };

  if (sortOptions.property === 'resourceType') {
    const filteredItem = _.orderBy(
      nextData?.data,
      [sortOptions.property],
      [sortOptions.direction],
    );
    nextData.data = filteredItem;
    return nextData;
  }

  const folders: Content[] = [];
  const playlists: Content[] = [];
  const presentations: Content[] = [];

  nextData?.data.forEach((item) => {
    switch (item.resourceType) {
      case 'folder':
        folders.push(item);
        break;
      case 'playlist':
        playlists.push(item);
        break;
      case 'presentation':
        presentations.push(item);
        break;
      default:
        break;
    }
  });

  const key = sortOptions.property as keyof Content;

  const sortedFolders = _.orderBy(
    folders,
    [(item) => (item[key] as string)?.toLowerCase()],
    [sortOptions.direction],
  );
  const sortedPlaylists = _.orderBy(
    playlists,
    [(item) => (item[key] as string)?.toLowerCase()],
    [sortOptions.direction],
  );
  const sortedPresentations = _.orderBy(
    presentations,
    [(item) => (item[key] as string)?.toLowerCase()],
    [sortOptions.direction],
  );

  nextData.data = [
    ...sortedFolders,
    ...sortedPlaylists,
    ...sortedPresentations,
  ];
  return nextData;
};

export const addQueryParamsToUrl = (baseUrl: string, queryParams: {}) => {
  return `${baseUrl}?${getQueryString(queryParams)}`;
};

export const changeV3DomainProfilesToV4 = (
  v3Response: ResourceProfile[],
): DomainUser[] => {
  return v3Response.map((profile) => ({
    id: profile.id,
    email: profile.email,
    name: profile.name,
    domainRole: profile.domainRole,
    createdAt: '',
    locations: [],
  }));
};

export const contentGrantLevel: ResourceGrant[] = [
  'read',
  'update',
  'share',
  'delete',
  'full',
];
